import {
  takeEvery, call, put, all, select, takeLatest
} from 'redux-saga/effects';
import { ActionTypes } from '../constants';
import utils from '../utils';
import Api from '../api';
import { history, registration } from 'shared/routes/urlLocations';
import notification from '../utils/notifications';
import { notification as antdNotification } from 'antd';
import { userHandlingAdminUsers, strategicPartnersSubscriptions } from 'shared/routes/urlLocations';
import _ from 'lodash';
import { isSp } from 'shared/utils/isSp';
import { ActionTypes as sharedActionTypes } from 'shared/constants';
import { urlPrefixValidation } from 'shared/utils';
import patternSaga from './patternSaga';

const apiUpdateUserInfo = `${utils.config.apiUserUrl}/user/update-by-admin`;

function* adminUserCreate({ values }) {
  try {
    let reCaptchaToken = sessionStorage.getItem("recaptchaToken");

    yield put({
      type: ActionTypes.ADMIN_USER_CREATE_STARTED,
      payload: {
        requestFrom: 'adminUserCreate'
      }
    });
    yield put({ type: ActionTypes.SHOW_SPINNER });

    const { response, request, status } = yield call(
      Api.User.adminUserCreate,
      `${utils.config.apiUserUrl}/register`,
      { ...values, userType: 'SUPER_ADMIN', type: 'USER', reCaptchaToken: reCaptchaToken }
    );

    if (request.status && (request.status === 200 || request.status === 204)) {
      yield put({
        type: ActionTypes.ADMIN_USER_CREATE_SUCCESS,
        message: response
      });
      const description = `Confirmation e-mail will be sent to ${values.email}`;
      yield call(notification, {
        type: 'success',
        data: { description }
      });
      yield history.push(userHandlingAdminUsers);
    } else {
      yield call(notification, {
        type: 'error',
        data: response,
        status
      });
    }
  } catch (error) {
    console.log('error: ', error);
    yield put({
      type: ActionTypes.ADMIN_USER_CREATE_FAILURE,
      message: error.message
    });
  } finally {
    yield put({ type: ActionTypes.HIDE_SPINNER });
    yield put({
      type: ActionTypes.ADMIN_USER_CREATE_FINISHED,
      payload: {
        requestFrom: 'adminUserCreate'
      }
    });
  }
}

const FILES_NAMES = {
  BACKGROUND_PICTURE: 'BACKGROUND_PICTURE',
  PRIVACY_POLICY: 'PRIVACY_POLICY',
  TERMS_OF_SERVICE: 'TERMS_OF_SERVICE',
  LOGO: 'LOGO',
  REGISTRATION_DOCUMENT: 'REGISTRATION_DOCUMENT',
  IDENTIFICATION_DOCUMENT: 'IDENTIFICATION_DOCUMENT'
};

const fieldFileTypeMap = {
  backgroundPicture: FILES_NAMES.BACKGROUND_PICTURE,
  privacyPolicyDocument: FILES_NAMES.PRIVACY_POLICY,
  termsOfServiceDocument: FILES_NAMES.TERMS_OF_SERVICE,
  logo: FILES_NAMES.LOGO,
  registrationDocument: FILES_NAMES.REGISTRATION_DOCUMENT,
  identificationDocument: FILES_NAMES.IDENTIFICATION_DOCUMENT
};

// Pulls a human-readable message out of the backend error payload, which for
// /register comes back as `[{ code, description }]` (but also handles a plain
// object / string just in case).
const extractSpErrorMessage = resp => {
  if (Array.isArray(resp)) {
    const msg = resp.map(item => item && item.description).filter(Boolean).join(', ');
    if (msg) return msg;
  } else if (resp && typeof resp === 'object') {
    if (resp.description) return resp.description;
    if (resp.message) return resp.message;
  } else if (typeof resp === 'string' && resp.trim()) {
    return resp;
  }
  return 'Failed to create the strategic partner. Please try again.';
};

function* strategicPartnerCreate({ values, url, isApplication }) {
  yield put({ type: ActionTypes.SHOW_SPINNER });

  const uploadTasks = Object.entries(values.files).reduce((acc, [field, file]) => {
    if (file) {
      acc.push(call(Api.Registration.uploadSPPlatformConfigFile, fieldFileTypeMap[field], file));
    }

    return acc;
  }, []);

  try {
    const results = yield all(uploadTasks);

    const isFilesUploadSuccess = _.every(
      results,
      ({ request }) => request.status && (request.status === 200 || request.status === 204)
    );

    if (isFilesUploadSuccess) {
      const filesData = results.reduce((acc, { response }) => {
        const fieldName = [_.invert(fieldFileTypeMap)[response.type]];
        return {
          ...acc,
          [fieldName]: response
        };
      }, {});

      values.files = undefined;

      const strategicPartnerModel = {
        ...values,
        ...filesData
      };

      const { response, request, status } = yield call(
        Api.User.strategicPartnerCreate,
        `${utils.config.apiUserUrl}/register`,
        {
          ...strategicPartnerModel,
          userType: 'ADMIN_STRATEGIC_PARTNER',
          type: 'COMPANY',
          status: isApplication ? 'REGISTERED' : undefined,
          reCaptchaToken: sessionStorage.getItem('recaptchaToken')
        }
      );

      if (request.status && (request.status === 200 || request.status === 204)) {
        yield put({
          type: ActionTypes.STRATEGIC_PARTNER_SUCCESS,
          message: response
        });
        // The confirmation e-mail is no longer sent at this step — the backend
        // now sends it on the step-2 user-details update. The "e-mail will be
        // sent" toast has moved to the SPKYCCHeck saga accordingly.

        yield history.push(
          `${strategicPartnersSubscriptions}/${
            response.id
          }/${url}?projectionName=companyRegistration`
        );
      } else {
        // The shared notification util routes through a MessagesQueue that can
        // silently swallow the call, so fire antd directly here (styled as an
        // error via `.error`) to guarantee the failure is shown.
        antdNotification.error({
          message: 'Error',
          description: extractSpErrorMessage(response),
          duration: 8,
          className: 'ant-notification-error'
        });
      }
    } else {
      const errorTasks = [];

      results.forEach(({ request, response }) => {
        if (!(request.status && (request.status === 200 || request.status === 204))) {
          errorTasks.push(
            call(notification, {
              type: 'error',
              data: response
            })
          );
        }
      });

      yield all(errorTasks);
    }
  } catch (e) {
    console.log(e);
    // Thrown failure (network error / malformed response) — show directly too.
    const errorData = e && e.response && (e.response.data || e.response);
    antdNotification.error({
      message: 'Error',
      description: extractSpErrorMessage(errorData),
      duration: 8,
      className: 'ant-notification-error'
    });
  }

  yield put({ type: ActionTypes.HIDE_SPINNER });
}

function* investorCompanyEdit(action) {
  try {
    yield put({
      type: ActionTypes.INVESTOR_COMPANY_EDIT_REQUEST_STARTED
    });
    yield put({ type: ActionTypes.SHOW_SPINNER });

    const { values } = action;
    const {
      user: { info }
    } = yield select();
    const params = { ...values };
    const { response, request, status } = yield call(
      Api.Registration.fullInvestorRegistration,
      `${utils.config.apiUserUrl}/edit`,
      params
    );
    const isSpCheck = yield call(isSp);
    const notificationClass = isSpCheck
      ? ""
      : "ant-notification-success ant-notification-notice-description";
    if (request.status && (request.status === 200 || request.status === 204)) {
      yield call(notification, {
        type: "success",
        className: notificationClass,
        data: { description: "Information edited successfully" }
      });
      yield put({
        type: sharedActionTypes.USER_INFORMATION_REQUEST
      });
    } else {
      yield call(notification, {
        type: "error",
        className: notificationClass,
        data: response
      });
    }
  } catch (e) {
    console.log("error: ", { e });
  } finally {
    yield put({ type: ActionTypes.HIDE_SPINNER });
    yield put({
      type: ActionTypes.INVESTOR_COMPANY_EDIT_REQUEST_FINISHED
    });
  }
}

function* investorIndividualEdit(action) {
  try {
    yield put({
      type: ActionTypes.INVESTOR_INDIVIDUAL_EDIT_REQUEST_STARTED
    });
    yield put({ type: ActionTypes.SHOW_SPINNER });

    const { values } = action;
    const {
      user: { info }
    } = yield select();
    const params = { ...values };
    if (typeof params.birthDate === "string") {
      params.birthDate = values.birthDate;
    } else {
      params.birthDate = values.birthDate.format("YYYY-MM-DD");
    }

    if (values.passportExpiry) {
      if (typeof params.passportExpiry === "string") {
        params.passportExpiry = values.passportExpiry;
      } else {
        params.passportExpiry = values.passportExpiry.format("YYYY-MM-DD");
      }
    }
    const { response, request, status } = yield call(
      Api.Registration.investorEdit,
      `${utils.config.apiUserUrl}/edit`,
      params
    );
    const isSpCheck = yield call(isSp);
    const notificationClass = isSpCheck
      ? ""
      : "ant-notification-success ant-notification-notice-description";
    if (request.status && (request.status === 200 || request.status === 204)) {
      yield call(notification, {
        type: "success",
        className: notificationClass,
        data: { description: "Information edited successfully"}
      });
      yield put({
        type: sharedActionTypes.USER_INFORMATION_REQUEST
      });
    } else {
      yield call(notification, {
        type: "error",
        className: notificationClass,
        data: response
      });
    }
  } catch (e) {
    console.log("error: ", { e });
  } finally {
    yield put({ type: ActionTypes.HIDE_SPINNER });
    yield put({
      type: ActionTypes.INVESTOR_INDIVIDUAL_EDIT_REQUEST_FINISHED
    });
  }
}

function* issuerCompanyEdit(data) {
  yield put({ type: ActionTypes.SHOW_SPINNER });
  const isSpCheck = yield call(isSp);
  const notificationClass = isSpCheck
    ? ""
    : "ant-notification-success ant-notification-notice-description";
  try {
    const { response, request, status } = yield call(
      Api.Registration.userRegistration,
      `${utils.config.apiUserUrl}/edit`,
      { data: { ...data.value, type: "COMPANY", userType: "ISSUER" } }
    );
    if (request.status && (request.status === 200 || request.status === 204)) {
      yield call(notification, {
        type: "success",
        className: notificationClass,
        data: { description: "Information edited successfully"}
      });
      yield put({
        type: sharedActionTypes.USER_INFORMATION_REQUEST
      });
    } else {
      yield call(notification, {
        type: "error",
        className: notificationClass,
        data: response
      });
    }
  } catch (e) {
    console.log("error: ", { e });
  } finally {
    yield put({ type: ActionTypes.HIDE_SPINNER });
  }
}

function* spAdminEdit(data) {
  yield put({ type: ActionTypes.SHOW_SPINNER });

  const isSpCheck = yield call(isSp);
  const notificationClass = isSpCheck ? "" : "ant-notification-success ant-notification-notice-description";

  try {
    const { response, request, status } = yield call(
      Api.Registration.userRegistration,
      `${utils.config.apiUserUrl}/edit-sp-admin`,
      {
        data: {
          ...data.values,
          type: "COMPANY",
          userType: "ADMIN_STRATEGIC_PARTNER"
        }
      }
    );

    if (request.status && (request.status === 200 || request.status === 204)) {
      yield call(notification, {
        type: "success",
        className: notificationClass,
        data: { description: "Edits saved successfully." }
      });
      yield put({ type: sharedActionTypes.USER_INFORMATION_REQUEST });
    } else {
      yield call(notification, {
        type: "error",
        className: notificationClass,
        data: response
      });
    }
  } catch (e) {
    console.log("error: ", { e });
  } finally {
    yield put({ type: ActionTypes.HIDE_SPINNER });
  }
}

function* FilesUpload({ values, successAction }) {
  yield put({ type: ActionTypes.SHOW_SPINNER });
  const isSpCheck = yield call(isSp);
  const notificationClass = isSpCheck
    ? ""
    : "ant-notification-success ant-notification-notice-description";

  const uploadTasks = [];


  if (values.identificationDocument && values.identificationDocument instanceof File) {
    uploadTasks.push(
      call(
        Api.Registration.uploadFiles,
        "IDENTIFICATION_DOCUMENT", // Replace with correct file type if needed
        values.identificationDocument
      )
    );
  }

  if (values.registrationDocument && values.registrationDocument instanceof File) {
    console.log("values.registrationDocument: ", values.registrationDocument);
    uploadTasks.push(
      call(
        Api.Registration.uploadFiles,
        "REGISTRATION_DOCUMENT",
        values.registrationDocument
      )
    );
  }

  try {
    const results = yield all(uploadTasks);

    console.log("4")
    const isFilesUploadSuccess = _.every(
      results,
      ({ request }) => request.status && (request.status === 200 || request.status === 204)
    );

    if (isFilesUploadSuccess) {
      const filesData = results.reduce((acc, { response }) => {
        const fieldName = [_.invert(fieldFileTypeMap)[response.type]];
        return {
          ...acc,
          [fieldName]: response
        };
      }, {});

      values.files = undefined;
      console.log(filesData);
      const advisorModel = {
        ...values,
        ...filesData
      };
      console.log("successAction: ", successAction);
      yield call(() => successAction(advisorModel));
    } else {
      const errorTasks = [];
      results.forEach(({ request, response }) => {
        if (!(request.status && (request.status === 200 || request.status === 204))) {
          errorTasks.push(
            call(notification, {
              type: "error",
              className: notificationClass,
              data: response
            })
          );
        }
      });

      yield all(errorTasks);
    }
  } catch (e) {
    console.log(e);
  }

  yield put({ type: ActionTypes.HIDE_SPINNER });
}


function* SPKYCCHeck({ payload }) {
  const { id, values, type } = payload;

  try {
    console.log("action called in saga", type, id, values);

    const updatedValues = {
    ...values,
    website: urlPrefixValidation(values.website)
  };

    console.log("Updated values for API:", updatedValues);

    yield put({ type: ActionTypes.STRATEGIC_PARTNER_KYC_CHECK_REQUEST_STARTED });
    yield put({ type: ActionTypes.SHOW_SPINNER });

    const apiResult = yield call(
    Api.User.SPKYCcheck,
    `${utils.config.apiUserUrl}/user/update-by-admin/${id}`,
    updatedValues
  );

    console.log("API Result:", apiResult);

    // Extract request info for status handling
    const { response, request, status } = apiResult;

    const isSpCheck = yield call(isSp);
    const notificationClass = isSpCheck
      ? ''
      : 'ant-notification-success ant-notification-notice-description';

    if (request && (request.status === 200 || request.status === 204)) {
      yield put({ type: sharedActionTypes.USER_INFORMATION_REQUEST });
      // The backend sends the SP confirmation e-mail on this step-2 update. The
      // recipient isn't on this form, so read it off the loaded SP user record
      // (KYCStatus, fetched on the page's mount). Fall back to a generic line.
      const kycUser = yield select(state => state.user && state.user.KYCStatus);
      const spEmail = kycUser && kycUser.email;
      const description = spEmail
        ? `Information was saved. Confirmation e-mail will be sent to ${spEmail}`
        : 'Information was saved.';
      yield call(notification, {
        type: 'success',
        className: notificationClass,
        data: { description }
      });
    } else if (request && request.status === 401) {
      yield call(notification, { type: 'error', data: response, status, className: notificationClass });
      yield put({ type: ActionTypes.USER_LOGOUT_REQUEST });
    } else {
      yield call(notification, { type: 'error', data: response, className: notificationClass });
    }

  } catch (error) {
    console.error("Saga caught error:", error);
    yield put({
      type: ActionTypes.STRATEGIC_PARTNER_KYC_CHECK_REQUEST_FAILURE,
      message: error.message
    });
  } finally {
    yield put({ type: ActionTypes.STRATEGIC_PARTNER_KYC_CHECK_REQUEST_FINISHED });
    yield put({ type: ActionTypes.HIDE_SPINNER });
  }
}


function* getSPCompanyKYCSaga(action) {
  const { userId } = action.payload;
  
  if (!userId) {
    console.error("userId is required to fetch SP KYC");
    return;
  }


  try {
    yield put({ type: ActionTypes.SP_ADMIN_KYC_REQUEST_STARTED });

    const url = `${utils.config.apiUserUrl}/user/sp/kyc-data/${userId}`;

    const apiResult = yield call(Api.User.getSPCompanyKYC, url);

    const { response, request } = apiResult;

    if (request && (request.status === 200 || request.status === 204)) {
      yield put({
        type: ActionTypes.SP_ADMIN_KYC_REQUEST_SUCCESS,
        payload: response
      });
    } else {
      console.error('SP KYC fetch failure:', response);
      yield put({
        type: ActionTypes.SP_ADMIN_KYC_REQUEST_FAILURE,
        payload: response
      });
    }
  } catch (error) {
    console.error('SP KYC fetch error:', error);
    yield put({
      type: ActionTypes.SP_ADMIN_KYC_REQUEST_FAILURE,
      payload: error.message
    });
  } finally {
    yield put({ type: ActionTypes.SP_ADMIN_KYC_REQUEST_FINISHED });
  }
}



export default function* watchUserRegistrationSaga() {
  yield takeEvery(ActionTypes.ADMIN_USER_CREATE, adminUserCreate);
  yield takeEvery(ActionTypes.STRATEGIC_PARTNER, strategicPartnerCreate);
  yield takeLatest(ActionTypes.INVESTOR_COMPANY_EDIT_REQUEST, investorCompanyEdit);
  yield takeLatest(ActionTypes.INVESTOR_INDIVIDUAL_EDIT_REQUEST, investorIndividualEdit);
  yield takeLatest(ActionTypes.ISSUER_EDIT_REQUEST, issuerCompanyEdit); 
  yield takeLatest(ActionTypes.STRATEGIC_PARTNER_ADMIN_EDIT_REQUEST, spAdminEdit);
  yield takeLatest(ActionTypes.FILES_UPLOAD, FilesUpload);
  yield takeLatest(ActionTypes.STRATEGIC_PARTNER_KYC_CHECK_REQUEST, SPKYCCHeck);
  yield takeLatest(ActionTypes.SP_ADMIN_KYC_REQUEST, getSPCompanyKYCSaga);


}
